import { FaIcon } from '../common/FaIcon';
import {
  faAddressCard,
  faArrowRightFromBracket,
  faBuilding,
  faCalendarDays,
  faChartColumn,
  faChartLine,
  faClipboardUser,
  faGear,
  faHelmetSafety,
  faIndustry,
  faLaptopCode,
  faListCheck,
  faPeopleRoof,
  faRoadBarrier,
  faScrewdriverWrench,
  faSignInAlt,
  faTriangleExclamation,
  faTruckField,
  faUser,
  faUserLock,
  faUserTie,
  faWarehouse,
} from '@fortawesome/free-solid-svg-icons';

import { QcsCompanySettings } from '../qcsCompanySettings/QcsCompanySettings';
import { Settings } from '../settings/Settings';
import { Dashboard } from '../dashboard/Dashboard';
import { SafetyEquipment } from '../safetyEquipment/SafetyEquipment';
import { Documents } from '../documents/Documents';
import { Employee } from '../employee/Employee';
import { User } from '../user/User';
import { Establishment } from '../establishment/Establishment';
import { Activity } from '../activity/Activity';
import { Risk } from '../risk/Risk';
import { Workplace } from '../workplace/Workplace';
import { Training } from '../training/Training';
import { Questionnaire } from '../questionnaire/Questionnaire';
import { Supplier } from '../supplier/Supplier';
import { Visitation } from '../visitation/Visitation';
import { Invitation } from '../invitation/Invitation';
import { QcsAdminCompany } from '../qcsAdmin/company/QcsAdminCompany';
import { QcsAdminUser } from '../qcsAdmin/user/QcsAdminUser';
import { QcsAdminSettings } from '../qcsAdmin/settings/QcsAdminSettings';

import { ActivityDetail } from '../activity/ActivityDetail';
import { DocumentDetail } from '../documents/DocumentDetail';
import { QcsAdminCompanyDetail } from '../qcsAdmin/company/QcsAdminCompanyDetail';
import { QcsAdminUserDetail } from '../qcsAdmin/user/QcsAdminUserDetail';
import { SafetyEquipmentDetail } from '../safetyEquipment/SafetyEquipmentDetail';
import { EstablishmentDetail } from '../establishment/EstablishmentDetail';
import { UserDetail } from '../user/UserDetail';
import { Profile } from '../user/Profile';
import { EmployeeDetail } from '../employee/EmployeeDetail';
import { RiskVersionDetail } from '../risk/RiskVersionDetail';
import { RiskDetail } from '../risk/RiskDetail';
import { SupplierDetail } from '../supplier/SupplierDetail';
import { SupplierEmployeeDetail } from '../supplier/SupplierEmployeeDetail';
import { SupplierRequestDetail } from '../supplier/SupplierRequestDetail';
import { SupplierCarDetail } from '../supplier/SupplierCarDetail';
import { WorkplaceDetail } from '../workplace/WorkplaceDetail';
import { Emergency } from '../emergency/Emergency';
import { TrainingDetail } from '../training/TrainingDetail';
import { TrainingVersionDetailData } from '../training/TrainingVersionDetailData';
import { TrainingSlidesEditor } from '../training/trainingSlidesEditor/TrainingSlidesEditor';
import { QuestionnaireDetail } from '../questionnaire/QuestionnaireDetail';
import { InvitationCreate } from '../invitation/InvitationCreate';
import { QuestionnaireVersionDetailData } from '../questionnaire/QuestionnaireVersionDetailData';
import { QuestionnaireQuestions } from '../questionnaire/QuestionnaireQuestions';
import { InvitationDetail } from '../invitation/InvitationDetail';
import { EmergencyNew } from '../emergency/EmergencyNew';
import { EmergencyDetail } from '../emergency/EmergencyDetail';
import { VisitationDetail } from '../visitation/VisitationDetail';
import { VisitationVisitDetailForm } from '../visitation/VisitationVisitDetailForm';
import { TrainingExternalList } from '../external/TrainingExternalList';
import { PinExternal } from '../external/PinExternal';
import { InvitationTypeVisit } from '../invitationTypeVisit/InvitationTypeVisit';
import { InvitationTypeVisitDetail } from '../invitationTypeVisit/InvitationTypeVisitDetail';
import { SupplierEmployeeFromVisitorDetail } from '../supplier/SupplierEmployeeFromVisitor';
import { VisitationTurnstileLogs } from '../visitation/VisitationTurnstileLogs';
import { EntryRequests } from '../entryRequest/EntryRequests';
import { Analytics } from '../analytics/Analytics';
import { CompanyBusinessSettings } from '@api/models/settings/CompanyBusinessSettings';
import { UserRole } from '@api/models/enums/UserRole';

interface MenuItem {
  isDetail?: boolean;
  id: string;
  link: string;
  icon?: React.ReactNode;
  roles: UserRole[];
  hasBusinessSettings?: keyof CompanyBusinessSettings;
  component?: React.ReactNode;
}

export const qcsAdminMenuItems: MenuItem[] = [
  {
    id: 'qcscompany',
    link: '',
    icon: <FaIcon icon={faBuilding} />,
    roles: [],
    component: <QcsAdminCompany />,
  },
  {
    isDetail: true,
    id: 'qcscompanyDetail',
    link: 'qcscompany/:companyId',
    roles: [],
    component: <QcsAdminCompanyDetail />,
  },
  {
    id: 'qcsuser',
    link: 'qcsuser',
    icon: <FaIcon icon={faUser} />,
    roles: [],
    component: <QcsAdminUser />,
  },
  {
    isDetail: true,
    id: 'qcsuserDetail',
    link: 'qcsuser/:userId',
    roles: [],
    component: <QcsAdminUserDetail />,
  },
  {
    id: 'qcssettings',
    link: 'qcssettings',
    icon: <FaIcon icon={faGear} />,
    roles: [],
    component: <QcsAdminSettings />,
  },
];

export const mainMenuItems: MenuItem[] = [
  {
    id: 'dashboard',
    link: '',
    icon: <FaIcon icon={faChartColumn} />,

    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <Dashboard />,
  },
  {
    id: 'invitation',
    link: 'invitation',
    icon: <FaIcon icon={faCalendarDays} />,
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
      UserRole.ExternalOneTime,
    ],
    component: <Invitation />,
  },
  {
    isDetail: true,
    id: 'invitationCreate',
    link: 'invitation/new',
    roles: [
      UserRole.AdminQcs,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
      UserRole.ExternalOneTime,
    ],
    component: <InvitationCreate />,
  },
  {
    isDetail: true,
    id: 'invitationDetail',
    link: 'invitation/:invitationId',
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
      UserRole.ExternalOneTime,
    ],
    component: <InvitationDetail />,
  },
  {
    isDetail: true,
    id: 'invitationDetailTab',
    link: 'invitation/:invitationId/:tabId',
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
      UserRole.ExternalOneTime,
    ],
    component: <InvitationDetail />,
  },
  {
    id: 'entryRequests',
    link: 'entryRequest',
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
    ],
    icon: <FaIcon icon={faSignInAlt} />,
    component: <EntryRequests />,
    hasBusinessSettings: 'allowEntryRequests',
  },
  {
    id: 'visitation',
    link: 'visitation',
    icon: <FaIcon icon={faPeopleRoof} />,
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
    ],
    component: <Visitation />,
  },
  {
    id: 'accessPointLogsLogs',
    link: 'access-point-logs',
    isDetail: true,
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
    ],
    component: <VisitationTurnstileLogs />,
  },
  {
    isDetail: true,
    id: 'visitationDetail',
    link: 'visitation/:visitationId',
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
    ],
    component: <VisitationDetail />,
  },
  {
    isDetail: true,
    id: 'visitationDetailVisit',
    link: 'visitation/visit/:invitationId',
    roles: [
      UserRole.AdminQcs,
      UserRole.AdminCompany,
      UserRole.ManagerOsah,
      UserRole.ManagerWorkplace,
      UserRole.Receptionist,
      UserRole.ExternalManager,
      UserRole.ExternalWorker,
    ],
    component: <VisitationVisitDetailForm />,
  },
  {
    id: 'establishment',
    link: 'establishment',
    icon: <FaIcon icon={faIndustry} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <Establishment />,
  },
  {
    isDetail: true,
    id: 'establishmentDetail',
    link: 'establishment/:establishmentId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <EstablishmentDetail />,
  },
  {
    id: 'workplace',
    link: 'workplace',
    icon: <FaIcon icon={faWarehouse} />,
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <Workplace />,
  },
  {
    isDetail: true,
    id: 'workplaceDetail',
    link: 'workplace/:workplaceId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <WorkplaceDetail />,
  },
  {
    id: 'employee',
    link: 'employee',
    icon: <FaIcon icon={faClipboardUser} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <Employee />,
  },
  {
    isDetail: true,
    id: 'employeeDetail',
    link: 'employee/:employeeId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <EmployeeDetail />,
  },
  {
    id: 'user',
    link: 'user',
    icon: <FaIcon icon={faUser} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <User />,
  },
  {
    isDetail: true,
    id: 'userDetail',
    link: 'user/:userId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <UserDetail />,
  },
  {
    isDetail: true,
    id: 'profile',
    link: 'profile',
    roles: [],
    component: <Profile />,
  },
  {
    id: 'risk',
    link: 'risk',
    icon: <FaIcon icon={faRoadBarrier} />,
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <Risk />,
  },
  {
    isDetail: true,
    id: 'riskDetail',
    link: 'risk/:riskId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <RiskDetail />,
  },
  {
    isDetail: true,
    id: 'riskVersionDetail',
    link: 'risk/:riskId/:riskVersionId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <RiskVersionDetail />,
  },
  {
    id: 'training',
    link: 'training',
    icon: <FaIcon icon={faLaptopCode} />,
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <Training />,
  },
  {
    isDetail: true,
    id: 'trainingDetail',
    link: 'training/:trainingId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <TrainingDetail />,
  },
  {
    isDetail: true,
    id: 'trainingVersionDetail',
    link: 'training/:trainingId/:trainingVersionId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <TrainingVersionDetailData />,
  },
  {
    isDetail: true,
    id: 'trainingSlides',
    link: 'training/:trainingId/:trainingVersionId/:languageCode',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <TrainingSlidesEditor />,
  },
  {
    id: 'questionnaire',
    link: 'questionnaire',
    icon: <FaIcon icon={faListCheck} />,
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <Questionnaire />,
  },
  {
    isDetail: true,
    id: 'questionnaireDetail',
    link: 'questionnaire/:questionnaireId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <QuestionnaireDetail />,
  },
  {
    isDetail: true,
    id: 'questionnaireDetail',
    link: 'questionnaire/:questionnaireId/:questionnaireVersionId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <QuestionnaireVersionDetailData />,
  },
  {
    isDetail: true,
    id: 'questionnaireQuestions',
    link: 'questionnaire/:questionnaireId/:questionnaireVersionId/:languageCode',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <QuestionnaireQuestions />,
  },
  {
    id: 'safetyEquipment',
    link: 'safety-equipment',
    icon: <FaIcon icon={faHelmetSafety} />,
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <SafetyEquipment />,
  },
  {
    isDetail: true,
    id: 'safetyEquipmentDetail',
    link: 'safety-equipment/:safetyEquipmentId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <SafetyEquipmentDetail />,
  },
  {
    id: 'invitationTypeVisit',
    link: 'visit',
    icon: <FaIcon icon={faUserTie} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <InvitationTypeVisit />,
  },
  {
    isDetail: true,
    id: 'invitationTypeVisitDetail',
    link: 'visit/:supplierId/employee/:supplierEmployeeId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <InvitationTypeVisitDetail />,
  },
  {
    id: 'supplier',
    link: 'supplier',
    icon: <FaIcon icon={faTruckField} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <Supplier />,
  },
  {
    isDetail: true,
    id: 'supplierDetail',
    link: 'supplier/:supplierId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <SupplierDetail />,
  },
  {
    isDetail: true,
    id: 'supplierDetailTab',
    link: 'supplier/:supplierId/:tabId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <SupplierDetail />,
  },
  {
    isDetail: true,
    id: 'supplierEmployeeDetail',
    link: 'supplier/:supplierId/employee/:supplierEmployeeId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <SupplierEmployeeDetail />,
  },
  {
    isDetail: true,
    id: 'SupplierEmployeeFromVisitorDetail',
    link: 'supplier/:supplierId/employee-from-visitor/:supplierEmployeeId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <SupplierEmployeeFromVisitorDetail />,
  },
  {
    isDetail: true,
    id: 'supplierCarDetail',
    link: 'supplier/:supplierId/cars/:supplierCarId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <SupplierCarDetail />,
  },
  {
    isDetail: true,
    id: 'supplierRequestNew',
    link: 'invitation/:invitationId/supplier-request/new',
    roles: [UserRole.ExternalManager],
    hasBusinessSettings: 'allowSubcontractors',
    component: <SupplierRequestDetail />,
  },
  {
    isDetail: true,
    id: 'supplierRequestApproval',
    link: 'invitation/:invitationId/supplier-request/:supplierId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace],
    hasBusinessSettings: 'allowSubcontractors',
    component: <SupplierRequestDetail />,
  },
  {
    id: 'supplierExternal',
    link: 'supplier',
    icon: <FaIcon icon={faTruckField} />,
    roles: [UserRole.ExternalManager],
    component: <SupplierDetail />,
  },
  {
    isDetail: true,
    id: 'supplierExternalTab',
    link: 'supplier/:tabId',
    roles: [UserRole.ExternalManager],
    component: <SupplierDetail />,
  },
  {
    isDetail: true,
    id: 'supplierExternalEmployeeDetail',
    link: 'supplier/employee/:supplierEmployeeId',
    roles: [UserRole.ExternalManager],
    component: <SupplierEmployeeDetail />,
  },
  {
    isDetail: true,
    id: 'supplierExternalCarDetail',
    link: 'supplier/cars/:supplierCarId',
    roles: [UserRole.ExternalManager],
    component: <SupplierCarDetail />,
  },
  {
    id: 'activity',
    link: 'activity',
    icon: <FaIcon icon={faScrewdriverWrench} />,
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <Activity />,
  },
  {
    isDetail: true,
    id: 'activityDetail',
    link: 'activity/:activityId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <ActivityDetail />,
  },
  {
    id: 'documents',
    link: 'documents',
    icon: <FaIcon icon={faAddressCard} />,
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <Documents />,
  },
  {
    isDetail: true,
    id: 'documentDetail',
    link: 'documents/:documentId',
    roles: [UserRole.AdminQcs, UserRole.ManagerOsah],
    component: <DocumentDetail />,
  },
  {
    id: 'emergency',
    link: 'emergency',
    icon: <FaIcon icon={faTriangleExclamation} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <Emergency />,
  },
  {
    isDetail: true,
    id: 'emergencyNew',
    link: 'emergency/new',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <EmergencyNew />,
  },
  {
    isDetail: true,
    id: 'emergencyDetail',
    link: 'emergency/:emergencyId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <EmergencyDetail />,
  },
  {
    isDetail: true,
    id: 'emergencyDetail2',
    link: 'emergency/:emergencyId/:scrollTo',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist],
    component: <EmergencyDetail />,
  },
  {
    id: 'analytics',
    link: 'analytics',
    icon: <FaIcon icon={faChartLine} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.Analyst],
    component: <Analytics />,
    hasBusinessSettings: 'allowAnalytics',
  },
  {
    isDetail: true,
    id: 'analyticsDetail',
    link: 'analytics/:domain',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.Analyst],
    component: <Analytics />,
    hasBusinessSettings: 'allowAnalytics',
  },
  {
    id: 'settings',
    link: 'settings',
    icon: <FaIcon icon={faGear} />,
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <Settings />,
  },
  {
    isDetail: true,
    id: 'settingsTab',
    link: 'settings/:tabId',
    roles: [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah],
    component: <Settings />,
  },
  {
    id: 'qcsCompanySettings',
    link: 'qcscompany-settings',
    icon: <FaIcon icon={faGear} />,
    roles: [UserRole.AdminQcs],
    component: <QcsCompanySettings />,
  },
  {
    id: 'training',
    link: 'training',
    icon: <FaIcon icon={faLaptopCode} />,
    roles: [UserRole.ExternalManager, UserRole.ExternalWorker, UserRole.ExternalOneTime],
    component: <TrainingExternalList />,
  },
  {
    id: 'pinExternal',
    link: 'pin',
    icon: <FaIcon icon={faUserLock} />,
    roles: [UserRole.ExternalManager, UserRole.ExternalWorker, UserRole.ExternalOneTime],
    component: <PinExternal />,
  },
];

export const userMenuItems: MenuItem[] = [
  {
    id: 'logout',
    link: 'login',
    icon: <FaIcon icon={faArrowRightFromBracket} />,
    roles: [],
  },
];
